/**
 * Run Rewards (after a key; mockup docs/art/mockups/reward.jpg): the party live on the stage,
 * the result banner (TIMED +2 · time · chest stars), one row of at most 6 reward slots that spill
 * in and count up (click or Space finishes), one line of three chips (world finds, Daily
 * Offering, Weekly Ritual), the rating line, and GO TO FORGE / Back to Town. Enter runs the key
 * again. The run stats and the meters live behind (i).
 *
 * Routed as `reward` (root-less: back / ⇥ go to the lobby). main.ts opens it with
 * `router.reset(ROUTES.reward, { summary, end, claims, again })`.
 */
import type { RunResult } from '@keystone/sim';
import { RANK_TIERS, dailyStatus, type RankStanding } from '@keystone/progression';
import type { MeterMode, MeterRow, ScoreRow } from '../../game/meters';
import { METER_MODES } from '../../game/meters';
import { clock } from '../../hud/format';
import { playSound } from '../../audio/engine';
import type { MetaScreenContext, MetaScreenFactory } from '../context';
import { ROUTES } from '../router';
import type { AutoClaims, RunSummary } from '../run';
import { heroFor } from '../heroes';
import { partyClasses } from '../run';
import { localDateString, prefersReducedMotion } from '../forge/localDate';
import { RESOURCE_META } from '../forge/resourceText';
import { el, roman } from '../ui/dom';
import { attachHoverCard, classCrest, divider, ghostButton, iconTabs, kmIcon, kmToast, primaryButton, secondaryButton } from '../kmmo';
import { heroStageOrCrest, metaShell, resourceSlot } from '../kmmo/extra-lobby';
import { forgeAttention } from './forgeModel';
import { countAt, rewardSlots, worldLine, type RewardSlot } from './offeringModel';
import './reward.css';
import { coachPanel } from '../../tutorial/coachPanel'; // +tutorial

export interface RewardParams {
  summary: RunSummary;
  end: { result: RunResult; dungeonName: string; rows: Record<MeterMode, MeterRow[]>; scoreboard?: ScoreRow[] };
  claims: Pick<AutoClaims, 'vault' | 'weekly'> | null;
  /** Run the same key again. */
  again?: () => void;
}

const tierName = (s: Pick<RankStanding, 'tier'>): string => RANK_TIERS.find((t) => t.tier === s.tier)?.name ?? s.tier;
const standingText = (s: Pick<RankStanding, 'tier' | 'subRank'>): string => `${tierName(s)} ${roman(s.subRank)}`;
const fmt = (n: number): string => Math.round(n).toLocaleString('en-US');

/** Result banner text: "TIMED +2", "DEPLETED", "ABANDONED". */
export function resultTitle(r: Pick<RunResult, 'timed' | 'completed' | 'chest'>): { text: string; plus: string; tone: 'timed' | 'depleted' | 'abandoned' } {
  if (r.timed) return { text: 'Timed', plus: r.chest > 0 ? `+${r.chest}` : '', tone: 'timed' };
  if (r.completed) return { text: 'Depleted', plus: '', tone: 'depleted' };
  return { text: 'Abandoned', plus: '', tone: 'abandoned' };
}

function slotLabel(s: RewardSlot): string {
  if (s.key === 'more') return 'More';
  const m = RESOURCE_META[s.key];
  return `${s.n === 1 ? m.one : m.label} x${fmt(s.n)}`;
}

export const rewardScreen: MetaScreenFactory = (ctx) => {
  const prm = ctx.params as unknown as Partial<RewardParams>;
  if (!prm.summary || !prm.end) {
    // Nothing to show (e.g. a refresh after the data was dropped): go home.
    queueMicrotask(() => ctx.router.reset());
    return { el: document.createElement('div') };
  }
  const s = prm.summary;
  const r = prm.end.result;
  const o = s.outcome;
  const shell = metaShell(ctx, {
    title: 'Run Rewards',
    center: `${prm.end.dungeonName}  +${r.level}`,
    onBack: () => ctx.router.reset(),
    info: () => detailsSheet(prm as RewardParams),
    className: 'krw',
  });
  const f = shell.frame;
  const stage = heroStageOrCrest(ctx, f.backdrop, { heroes: partyClasses(ctx.store.profile), centerX: 0.5, fill: 0.7, feetY: 0.84, gap: 1.45, turn: 0, interactive: false, ring: false, reflect: true });
  const body = f.body;

  // ---- result banner
  const res = resultTitle(r);
  const top = el('div', 'krw-top', body);
  const banner = el('div', `krw-banner ${res.tone}`, top);
  el('span', 'krw-banner-text', banner, res.text);
  if (res.plus) el('span', 'krw-banner-plus', banner, res.plus);
  el('div', 'krw-time km-num', top, `${clock(r.elapsedMs)} / ${clock(r.timerMs)}`);
  const stars = el('div', 'krw-stars', top);
  for (let i = 0; i < 3; i++) el('span', `krw-star${i < (r.timed ? r.chest : 0) ? ' on' : ''}`, stars).appendChild(kmIcon('star'));
  const hero = heroFor(s.mainClassId);
  const heroLine = el('div', 'krw-heroline', top, `${hero.name} · +${fmt(s.xp)} XP`);
  if (s.level.after > s.level.before) {
    el('span', 'krw-lvup', heroLine, `Lv ${s.level.after}!`);
    playSound('level.up');
  }

  // ---- the card
  const cardEl = el('section', 'krw-card km-panel km-panel-solid km-ornate', body);
  // Scoreboard first (who did what this run), then the rewards tally (starts when first shown).
  const tabs = iconTabs(
    [
      { id: 'score', label: 'Scoreboard' },
      { id: 'rewards', label: 'Rewards' },
    ],
    { active: 'score', style: 'text', onChange: (id) => showPane(id) },
    cardEl,
  );
  tabs.el.classList.add('krw-tabs');
  const scorePane = el('div', 'krw-pane krw-score', cardEl);
  scoreboardView(ctx, scorePane, prm as RewardParams);
  const card = el('div', 'krw-pane krw-rewards hidden', cardEl);
  const slotsRow = el('div', 'krw-slots', card);
  const slots = rewardSlots({ payout: s.payout, gold: s.gold, vault: prm.claims?.vault?.bundle ?? null, weekly: prm.claims?.weekly?.bundle ?? null });
  const counters: { set(n: number): void; n: number; el: HTMLElement }[] = [];
  if (!slots.length) el('div', 'krw-empty', slotsRow, r.completed ? 'Nothing this time.' : 'Abandoned keys pay no resources. Finish a key, timed or not, to earn them.');
  for (const sl of slots) {
    const h = sl.key === 'more' ? resourceSlot('more', 0, { size: 'lg', caption: slotLabel(sl), ribbon: sl.tag ?? undefined }, slotsRow) : resourceSlot(sl.key, 0, { size: 'lg', caption: slotLabel(sl), ribbon: sl.tag ?? undefined }, slotsRow);
    h.el.classList.add('krw-slot');
    if (sl.key === 'more') {
      const more = sl.more ?? [];
      el('span', 'krw-more-plus', h.slot, `+${more.length}`);
      attachHoverCard(h.slot, () => more.map((a) => `${RESOURCE_META[a.key].label} x${fmt(a.n)}`).join('  ·  '));
      counters.push({ n: 0, el: h.el, set() {} });
      continue;
    }
    let badge = h.slot.querySelector<HTMLElement>('.km-res-count');
    if (!badge) badge = el('span', 'km-badge-count km-num km-res-count', h.slot);
    const b = badge;
    counters.push({ n: sl.n, el: h.el, set: (n) => (b.textContent = fmt(n)) });
  }

  divider(card);
  const chips = el('div', 'krw-chips', card);
  const chip = (icon: Parameters<typeof kmIcon>[0], text: string, tone = ''): void => {
    const c = el('span', `krw-chip ${tone}`.trim(), chips);
    c.appendChild(kmIcon(icon));
    el('span', '', c, text);
  };
  const wl = worldLine(s.payout);
  chip('dungeon', wl?.stats?.length ? `Found in the dungeon: ${wl.stats.join(' · ')}` : 'Found in the dungeon: nothing');
  const date = localDateString();
  const dailyText = s.payout.daily ? 'Daily Offering claimed' : dailyStatus(ctx.store.profile.forge, date).ready ? 'Daily Offering ready' : 'Daily Offering claimed';
  chip('chest', dailyText, s.payout.daily ? 'good' : '');
  const wk = s.payout.weekly;
  chip('calendar', prm.claims?.weekly ? 'Weekly Ritual claimed' : `Weekly Ritual ${Math.min(wk?.runs ?? 0, 4)}/4`, prm.claims?.weekly ? 'good' : '');
  divider(card);

  // ---- rating
  const rating = el('div', 'krw-rating', card);
  classCrest('rank', { icon: 'rank', size: 'md', selected: true }, rating);
  const rt = el('div', 'krw-rating-text', rating);
  el('div', `krw-tier${s.tierChanged ? ' up' : ''}`, rt, s.tierChanged ? `Promoted to ${standingText(o.standingAfter)}!` : standingText(o.standingAfter));
  const line = el('div', 'krw-rating-line km-num', rt, `Rating ${fmt(o.ratingBefore)} → ${fmt(o.ratingAfter)} `);
  const d = Math.round(o.ratingAfter) - Math.round(o.ratingBefore);
  el('span', d > 0 ? 'up' : 'same', line, d > 0 ? `(+${d})` : '(±0)');

  // ---- actions
  coachPanel(scorePane); // +tutorial: 1–2 coaching tips for the first runs
  const acts = el('div', 'krw-actions', cardEl);
  secondaryButton('Back to Town', { onClick: () => ctx.router.reset() }, acts);
  let badge = 0;
  try {
    badge = forgeAttention(ctx.store.profile, date).badge;
  } catch {
    badge = 0;
  }
  const forge = primaryButton('Go to Forge', { size: 'lg', onClick: () => ctx.router.reset(ROUTES.forge) }, acts);
  if (badge > 0) el('span', 'km-dot', forge);
  if (prm.again) ghostButton(`Again (+${r.level})`, { size: 'sm', className: 'krw-again', title: 'Run this key again (Enter)', onClick: () => prm.again!() }, cardEl);

  // ---- tally (slots land one after another and count up)
  const reduced = prefersReducedMotion();
  const STEP = reduced ? 40 : 220;
  const COUNT = reduced ? 80 : 520;
  let raf = 0;
  let start: number | null = null;
  let done = false;
  const draw = (t: number): void => {
    counters.forEach((c, i) => {
      const at = 200 + i * STEP;
      if (t >= at) c.el.classList.add('in');
      c.set(countAt(c.n, at, COUNT, t));
    });
  };
  const end = 200 + counters.length * STEP + COUNT;
  const finish = (): void => {
    if (done) return;
    done = true;
    cancelAnimationFrame(raf);
    draw(end + 1);
  };
  const frame = (now: number): void => {
    start ??= now;
    const t = now - start;
    draw(t);
    if (t >= end) return finish();
    raf = requestAnimationFrame(frame);
  };
  let tallyStarted = false;
  function showPane(id: string): void {
    scorePane.classList.toggle('hidden', id !== 'score');
    card.classList.toggle('hidden', id !== 'rewards');
    if (id === 'rewards' && !tallyStarted) {
      tallyStarted = true;
      raf = requestAnimationFrame(frame);
    }
  }
  card.addEventListener('pointerdown', finish);
  const onKey = (e: KeyboardEvent): void => {
    if (e.defaultPrevented || document.querySelector('.km-modal-back')) return;
    if (e.code === 'Tab') {
      // Tab flips Scoreboard / Rewards.
      e.preventDefault();
      const next = tabs.active === 'score' ? 'rewards' : 'score';
      tabs.set(next);
      showPane(next);
    } else if (e.code === 'Space' && tallyStarted && !done) {
      e.preventDefault();
      finish();
    } else if ((e.key === 'Enter' || e.code === 'NumpadEnter') && prm.again && !(e.target instanceof HTMLButtonElement)) {
      e.preventDefault();
      prm.again();
    }
  };
  window.addEventListener('keydown', onKey);

  return {
    el: shell.el,
    dispose() {
      cancelAnimationFrame(raf);
      window.removeEventListener('keydown', onKey);
      stage.dispose();
      shell.dispose();
    },
  };
};

const METER_TITLE: Record<MeterMode, string> = { damage: 'Damage', healing: 'Healing', taken: 'Damage taken', interrupts: 'Interrupts' };

/** (i) sheet: the run stats and the four meters (top 4 each). */
function detailsSheet(p: RewardParams): HTMLElement {
  const r = p.end.result;
  const root = el('div', 'krw-details');
  const stats = el('div', 'krw-dstats', root);
  const stat = (label: string, value: string): void => {
    const s = el('div', 'krw-dstat', stats);
    el('b', 'km-num', s, value);
    el('span', '', s, label);
  };
  stat('Time', `${clock(r.elapsedMs)} / ${clock(r.timerMs)}`);
  stat('Deaths', String(r.deaths));
  stat('Lost to deaths', clock(r.deathPenaltyMs));
  stat('Out of combat', clock(r.outOfCombatMs));
  stat('Forces', `${r.forces}/${r.forcesRequired}`);
  stat('Pulls / wipes', `${r.pulls} / ${r.wipes}`);
  const meters = el('div', 'krw-meters', root);
  for (const m of METER_MODES) {
    const box = el('div', 'krw-meter', meters);
    el('div', 'krw-meter-title', box, METER_TITLE[m]);
    const rows = [...(p.end.rows[m] ?? [])].sort((a, b) => b.value - a.value).slice(0, 4);
    const max = Math.max(1, ...rows.map((x) => x.value));
    for (const row of rows) {
      const line = el('div', 'krw-meter-row', box);
      const bar = el('i', '', line);
      bar.style.width = `${(row.value / max) * 100}%`;
      bar.style.background = row.color;
      el('span', '', line, row.name);
      el('b', 'km-num', line, fmt(row.value));
    }
  }
  el('p', 'krw-dnote', root, 'Rating counts your best timed key in each dungeon. Timing a key unlocks the next level. Resources are paid for finishing a key (more when timed), plus whatever you found in the dungeon.');
  return root;
}

/** Scoreboard tab: run summary strip, then one row per hero with the run's totals; column bests in gold. */
function scoreboardView(ctx: MetaScreenContext, root: HTMLElement, p: RewardParams): void {
  const r = p.end.result;
  const rows = p.end.scoreboard ?? fromMeterRows(p.end.rows);
  const strip = el('div', 'krw-sstrip', root);
  const stat = (label: string, value: string, tone = ''): void => {
    const s = el('div', `krw-sstat ${tone}`.trim(), strip);
    el('b', 'km-num', s, value);
    el('span', '', s, label);
  };
  stat('Time', `${clock(r.elapsedMs)} / ${clock(r.timerMs)}`, r.timed ? 'good' : 'bad');
  stat('Deaths', r.deathPenaltyMs > 0 ? `${r.deaths} (−${clock(r.deathPenaltyMs)})` : String(r.deaths), r.deaths > 0 ? 'bad' : '');
  stat('Forces', `${Math.min(100, Math.round((r.forces / Math.max(1, r.forcesRequired)) * 100))}%`);
  stat('Pulls', String(r.pulls));
  stat('Kicks', String(rows.reduce((n, x) => n + x.interrupts, 0)));

  const best = (k: 'damage' | 'healing' | 'interrupts'): number => Math.max(0, ...rows.map((x) => x[k]));
  const top = { damage: best('damage'), healing: best('healing'), interrupts: best('interrupts') };
  const table = el('div', 'krw-stable', root);
  const head = el('div', 'krw-srow krw-shead', table);
  for (const h of ['Hero', 'Damage', 'Healing', 'Taken', 'Kicks', 'Deaths']) el('span', '', head, h);
  const bar = (cell: HTMLElement, v: number, max: number, color: string): void => {
    const b = el('i', 'krw-sbar', cell);
    b.style.width = `${max > 0 ? (v / max) * 100 : 0}%`;
    b.style.background = color;
  };
  for (const x of rows) {
    const row = el('div', `krw-srow${x.you ? ' you' : ''}`, table);
    const who = el('span', 'krw-swho', row);
    if (x.classId) classCrest(x.classId, { size: 'sm' }, who);
    const nm = el('span', 'krw-sname', who, x.name);
    nm.style.color = x.color;
    if (x.you) el('em', 'krw-syou', who, 'You');
    const dmg = el('span', `krw-snum${x.damage > 0 && x.damage === top.damage ? ' best' : ''}`, row);
    bar(dmg, x.damage, top.damage, x.color);
    el('b', 'km-num', dmg, shortNum(x.damage));
    el('small', 'km-num', dmg, `${shortNum(x.dps)}/s`);
    const heal = el('span', `krw-snum${x.healing > 0 && x.healing === top.healing ? ' best' : ''}`, row);
    bar(heal, x.healing, top.healing, x.color);
    el('b', 'km-num', heal, shortNum(x.healing));
    el('small', 'km-num', heal, `${shortNum(x.hps)}/s`);
    el('span', 'krw-snum km-num', row, shortNum(x.taken));
    el('span', `krw-snum km-num${x.interrupts > 0 && x.interrupts === top.interrupts ? ' best' : ''}`, row, String(x.interrupts));
    el('span', `krw-snum km-num${x.deaths > 0 ? ' bad' : ''}`, row, x.deaths < 0 ? '—' : String(x.deaths));
  }
}

/** Fallback when the run sent no scoreboard (older party server): rebuild it from the meter rows. */
function fromMeterRows(rows: Record<MeterMode, MeterRow[]>): ScoreRow[] {
  const by = new Map<string, ScoreRow>();
  for (const m of METER_MODES) {
    for (const x of rows[m] ?? []) {
      let s = by.get(x.unitId);
      if (!s) by.set(x.unitId, (s = { unitId: x.unitId, name: x.name, classId: null, color: x.color, damage: 0, healing: 0, taken: 0, interrupts: 0, deaths: -1, dps: 0, hps: 0, you: false }));
      s[m] = x.value;
      if (m === 'damage') s.dps = x.perSec;
      if (m === 'healing') s.hps = x.perSec;
    }
  }
  return [...by.values()];
}

/** 12.4K / 1.2M. */
function shortNum(n: number): string {
  const a = Math.abs(n);
  if (a >= 1e6) return `${(n / 1e6).toFixed(a >= 1e7 ? 0 : 1)}M`;
  if (a >= 1e4) return `${(n / 1e3).toFixed(a >= 1e5 ? 0 : 1)}K`;
  return fmt(n);
}
